import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useResource, formatDate, formatDateTime, daysUntil } from "../../lib/hooks";
import type { Incident, Policy } from "../../lib/types";

interface PolicyFull extends Policy {
    revoked_at: string | null;
    superseded_by_id: string | null;
    file_id: string | null;
}

interface IncidentFull extends Incident {
    notified_at: string | null;
    notify_due_at: string | null;
    submitted_at?: string | null;
}

/**
 * The governance roll-up the old CRM called Compliance: is the policy library
 * current, and are reportable incidents being notified on time. Everything is
 * derived live from the same rows the Policies and Incidents pages edit — a
 * compliance page with its own copy of the truth would drift, and a drifted
 * compliance page is worse than none.
 */
export const Compliance = () => {
    const policies = useResource<PolicyFull[]>("/policies");
    const incidents = useResource<IncidentFull[]>("/incidents");

    const policyStats = useMemo(() => {
        const all = policies.data ?? [];
        const current = all.filter((p) => p.status === "current" && !p.revoked_at && !p.superseded_by_id);
        return {
            total: all.length,
            current: current.length,
            reviewOverdue: current.filter((p) => p.review_date && daysUntil(p.review_date) < 0),
            reviewSoon: current.filter((p) => p.review_date && daysUntil(p.review_date) >= 0 && daysUntil(p.review_date) <= 30),
            missingFile: current.filter((p) => !p.file_id),
        };
    }, [policies.data]);

    const reportable = (incidents.data ?? []).filter((i) => i.is_ndis_reportable);
    const unnotified = reportable.filter((i) => !i.notified_at && i.status !== "closed");
    const openIncidents = (incidents.data ?? []).filter((i) => i.status === "open" || i.status === "under_review");

    if (policies.error) return <div className="error-banner">{policies.error}</div>;

    return (
        <div style={{ display: "grid", gap: "1.1rem" }}>
            <div className="grid cols-4">
                <div className="card stat">
                    <div className="label">Current policies</div>
                    <div className="value">{policyStats.current}</div>
                    <div className="sub">of {policyStats.total} in the library</div>
                </div>
                <div className="card stat">
                    <div className="label">Reviews overdue</div>
                    <div className="value" style={{ color: policyStats.reviewOverdue.length ? "var(--block)" : "var(--pass)" }}>
                        {policyStats.reviewOverdue.length}
                    </div>
                </div>
                <div className="card stat">
                    <div className="label">Open incidents</div>
                    <div className="value">{openIncidents.length}</div>
                </div>
                <div className="card stat">
                    <div className="label">Awaiting NDIS notification</div>
                    <div className="value" style={{ color: unnotified.length ? "var(--block)" : "var(--pass)" }}>
                        {unnotified.length}
                    </div>
                    <div className="sub">reportable, not yet notified</div>
                </div>
            </div>

            {unnotified.length > 0 && (
                <div className="card">
                    <div className="card-head">
                        <h2>Reportable incidents awaiting Commission notification</h2>
                        <span className="chip block">{unnotified.length} outstanding</span>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Incident</th><th>Occurred</th><th>Notification due</th><th>Status</th></tr></thead>
                                <tbody>
                                    {unnotified.map((incident) => (
                                        <tr key={incident.id}>
                                            <td style={{ fontWeight: 600 }}>{incident.title}</td>
                                            <td className="mono">{formatDateTime(incident.occurred_at)}</td>
                                            <td className="mono">
                                                {incident.notify_due_at ? formatDateTime(incident.notify_due_at) : "—"}
                                            </td>
                                            <td><span className="chip warn">{incident.status.replace("_", " ")}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", fontSize: "0.8rem", color: "var(--muted)" }}>
                        These incidents cannot be closed until the notification is recorded —
                        the system refuses the closure. <Link to="/incidents">Record it from Incidents</Link>.
                    </div>
                </div>
            )}

            <div className="grid cols-2">
                <div className="card">
                    <div className="card-head">
                        <h2>Policy reviews</h2>
                        <Link to="/policies" style={{ fontSize: "0.78rem" }}>Policy library →</Link>
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <tbody>
                                    {[...policyStats.reviewOverdue, ...policyStats.reviewSoon].map((policy) => {
                                        const overdue = daysUntil(policy.review_date!) < 0;
                                        return (
                                            <tr key={policy.id}>
                                                <td style={{ fontWeight: 600 }}>{policy.name} <span className="mono" style={{ color: "var(--muted)" }}>v{policy.version}</span></td>
                                                <td className="mono">{formatDate(policy.review_date)}</td>
                                                <td>
                                                    <span className={`chip ${overdue ? "block" : "warn"}`}>
                                                        {overdue ? "overdue" : `${daysUntil(policy.review_date!)}d`}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {policyStats.reviewOverdue.length + policyStats.reviewSoon.length === 0 && (
                                        <tr><td className="empty">No reviews due in the next 30 days.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head"><h2>Library gaps</h2></div>
                    <div className="card-body">
                        {policyStats.missingFile.length === 0 ? (
                            <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                                Every current policy has its document uploaded.
                            </p>
                        ) : (
                            <>
                                <p style={{ fontSize: "0.85rem", color: "var(--ink-soft)", marginBottom: "0.5rem" }}>
                                    Current policies with <strong>no document uploaded</strong> — staff are being
                                    asked to acknowledge text they cannot read:
                                </p>
                                <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.85rem" }}>
                                    {policyStats.missingFile.map((policy) => (
                                        <li key={policy.id}>{policy.name} v{policy.version}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
